import { type Clip, ClipType } from '@air/api/types';
import { isPdfRenderedExtension } from '@air/utils-clips';

export type WorkspaceAgentGuidedVariantSkipCounts = {
  skippedFileCount: number;
  skippedVideoCount: number;
};

type WorkspaceAgentGuidedVariantSkipClip = {
  ext?: string | null;
  type: Clip['type'];
};

export type WorkspaceAgentGuidedVariantSkipKind = 'file' | 'image' | 'video';

export const getWorkspaceAgentGuidedVariantSkipKind = ({
  ext,
  type,
}: WorkspaceAgentGuidedVariantSkipClip): WorkspaceAgentGuidedVariantSkipKind => {
  switch (type) {
    case ClipType.animated:
    case ClipType.livePhoto:
      return 'image';
    case ClipType.photo:
      // PDFs and office docs are stored as photo clips so they can render a still.
      return ext && isPdfRenderedExtension(ext) ? 'file' : 'image';
    case ClipType.video:
      return 'video';
    case ClipType.audio:
    case ClipType.nonMedia:
      return 'file';
  }
};

export type WorkspaceAgentGuidedVariantImageSelection = {
  imageCount: number;
  skippedFileCount: number;
  skippedVideoCount: number;
};

export type WorkspaceAgentGuidedVariantMediaSelection = {
  mediaCount: number;
  skippedFileCount: number;
};

/** Images count toward watermark, grayscale, rounded corners, and remove background. Videos and other files are skipped. */
export const getWorkspaceAgentGuidedVariantImageSelection = (
  clips: readonly WorkspaceAgentGuidedVariantSkipClip[],
): WorkspaceAgentGuidedVariantImageSelection => {
  let imageCount = 0;
  let skippedFileCount = 0;
  let skippedVideoCount = 0;

  for (const clip of clips) {
    const kind = getWorkspaceAgentGuidedVariantSkipKind(clip);

    if (kind === 'image') {
      imageCount += 1;
      continue;
    }

    if (kind === 'video') {
      skippedVideoCount += 1;
      continue;
    }

    skippedFileCount += 1;
  }

  return { imageCount, skippedFileCount, skippedVideoCount };
};

/** Images and videos count toward smart resize and scale or crop. Other files are skipped. */
export const getWorkspaceAgentGuidedVariantMediaSelection = (
  clips: readonly WorkspaceAgentGuidedVariantSkipClip[],
): WorkspaceAgentGuidedVariantMediaSelection => {
  let mediaCount = 0;
  let skippedFileCount = 0;

  for (const clip of clips) {
    const kind = getWorkspaceAgentGuidedVariantSkipKind(clip);

    if (kind === 'file') {
      skippedFileCount += 1;
      continue;
    }

    mediaCount += 1;
  }

  return { mediaCount, skippedFileCount };
};

export const getWorkspaceAgentGuidedVariantSkipCounts = (
  clips: readonly WorkspaceAgentGuidedVariantSkipClip[],
): WorkspaceAgentGuidedVariantSkipCounts => {
  let skippedFileCount = 0;
  let skippedVideoCount = 0;

  for (const clip of clips) {
    const kind = getWorkspaceAgentGuidedVariantSkipKind(clip);

    if (kind === 'video') {
      skippedVideoCount += 1;
    } else if (kind === 'file') {
      skippedFileCount += 1;
    }
  }

  return { skippedFileCount, skippedVideoCount };
};
